/** The Branding page's preview: the real calculator in "Sample home" demo mode (`/api/public/embed-demo`), whose
 * requests this page answers from the recorded sample homes, priced with the company's current settings
 * (lib/demoBridge.ts). Free: no pipeline, Google call, Lux or bot check, and no allowed website needed.
 *
 * The brand being edited reaches the frame as it changes (`DemoBridge.setBrand`), without reloading it. Visitors'
 * own buttons move through the flow. Inline the frame is phone-wide; "Full screen" turns the same frame (so the
 * step is kept) into a window-sized dialog at desktop width with only a Close button; Esc also closes it. */
import { useQuery } from "@tanstack/react-query";
import { Maximize2, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { api, errorMessage, unwrap, type S } from "../api/client";
import { publicApiOrigin } from "../config";
import { useT } from "../i18n/I18nProvider";
import { DemoBridge } from "../lib/demoBridge";
import { Alert, Spinner, cx } from "./ui";

function demoSrc(): string {
  return `${publicApiOrigin()}/api/public/embed-demo`;
}

function originOf(url: string): string {
  try {
    return new URL(url, window.location.href).origin;
  } catch {
    return "";
  }
}

export function CalculatorPreview({ orgId, brand }: { orgId: string; brand: S["BrandSettings"] }) {
  const t = useT();
  const demo = useQuery({
    queryKey: ["widget-demo", orgId],
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/widget-demo", { params: { path: { org_id: orgId } } })),
    staleTime: Infinity,
  });
  const frame = useRef<HTMLIFrameElement>(null);
  const openButton = useRef<HTMLButtonElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const [full, setFull] = useState(false);

  const bridge = useMemo(() => (demo.data ? new DemoBridge(orgId, demo.data) : null), [orgId, demo.data]);
  const close = useRef(() => setFull(false));
  useEffect(() => (bridge ? bridge.listen(() => frame.current, originOf(demoSrc()), () => close.current()) : undefined), [bridge]);
  useEffect(() => bridge?.setBrand(brand), [bridge, brand]);

  useEffect(() => {
    if (!full) return;
    closeButton.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setFull(false);
    window.addEventListener("keydown", onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
      openButton.current?.focus();
    };
  }, [full]);

  // Focus stays in the dialog: the sentinels around it send Tab back to Close or into the calculator.
  const trap = (to: "start" | "end") => () => (to === "start" ? closeButton.current : frame.current)?.focus();

  return (
    <div className="stack">
      <div className="spread">
        <h2>{t("branding.preview.title")}</h2>
        <button ref={openButton} type="button" className="btn btn-sm" onClick={() => setFull(true)} disabled={!demo.data}>
          <Maximize2 /> {t("branding.preview.fullScreen")}
        </button>
      </div>
      {demo.isLoading ? (
        <Spinner />
      ) : demo.error ? (
        <Alert tone="danger">{errorMessage(demo.error)}</Alert>
      ) : (
        <div
          className={cx("calc-preview", full && "full")}
          role={full ? "dialog" : undefined}
          aria-modal={full || undefined}
          aria-label={full ? t("branding.preview.dialog") : undefined}
        >
          {full && <span tabIndex={0} onFocus={trap("end")} />}
          {full && (
            <div className="calc-preview-bar">
              <span className="small muted">{t("branding.preview.bar")}</span>
              <button ref={closeButton} type="button" className="btn btn-sm" onClick={() => setFull(false)}>
                <X /> {t("branding.action.close")}
              </button>
            </div>
          )}
          <iframe ref={frame} src={demoSrc()} title={t("branding.preview.frame")} />
          {full && <span tabIndex={0} onFocus={trap("start")} />}
        </div>
      )}
      <p className="hint">{t("branding.preview.hint")}</p>
    </div>
  );
}
